import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import img from "@/assets/segnalazioni-smartphone.jpg";

const schema = z.object({
  tipo_barriera: z.string().min(1, "Seleziona un tipo"),
  descrizione: z.string().trim().min(10, "Descrizione troppo breve").max(2000),
  indirizzo: z.string().trim().min(3).max(200),
  citta: z.string().trim().min(2).max(100),
  provincia: z.string().trim().max(50).optional().or(z.literal("")),
  nome_segnalante: z.string().trim().max(100).optional().or(z.literal("")),
  email_segnalante: z.string().trim().email("Email non valida").max(255).optional().or(z.literal("")),
});

const tipi = [
  "Barriera architettonica fisica",
  "Marciapiede o attraversamento",
  "Edificio pubblico",
  "Trasporti pubblici",
  "Stabilimento balneare",
  "Struttura ricettiva",
  "Barriera culturale o comunicativa",
  "Altro",
];

const Segnalazioni = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    tipo_barriera: "",
    descrizione: "",
    indirizzo: "",
    citta: "",
    provincia: "",
    nome_segnalante: "",
    email_segnalante: "",
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const d = parsed.data;
    const { error } = await supabase.from("barrier_reports").insert({
      tipo_barriera: d.tipo_barriera,
      descrizione: d.descrizione,
      indirizzo: d.indirizzo,
      citta: d.citta,
      provincia: d.provincia || undefined,
      nome_segnalante: d.nome_segnalante || undefined,
      email_segnalante: d.email_segnalante || undefined,
    });
    setLoading(false);
    if (error) {
      toast.error("Errore nell'invio. Riprova.");
      return;
    }
    toast.success("Segnalazione inviata. Grazie per il tuo contributo!");
    setForm({ tipo_barriera: "", descrizione: "", indirizzo: "", citta: "", provincia: "", nome_segnalante: "", email_segnalante: "" });
  };

  return (
    <>
      <SEO
        title="Segnalazioni Barriere — FIABA ETS"
        description="Segnala una barriera architettonica nel tuo territorio. FIABA raccoglie le segnalazioni dei cittadini per favorire la rimozione delle barriere e migliorare l'accessibilità."
        path="/segnalazioni"
      />
      <PageHero
        eyebrow="Nuovo servizio — Back Office"
        title="Segnala una barriera"
        subtitle="Aiutaci a costruire una banca dati nazionale delle barriere architettoniche."
      />
      <section className="section-padding">
        <div className="container-narrow grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            <img src={img} alt="Persona che fotografa una barriera architettonica con lo smartphone" className="rounded-lg shadow-elegant" loading="lazy" />
            <p className="text-muted-foreground leading-relaxed">
              FIABA sta avviando una nuova campagna di sensibilizzazione e partecipazione attiva
              dei cittadini finalizzata alla creazione di una banca dati utile ed essenziale
              per identificare le barriere presenti negli ambienti pubblici.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              L'obiettivo è valorizzare le segnalazioni, raccogliere informazioni puntuali
              e favorire l'avvio dei processi necessari affinché le barriere individuate
              possano essere rimosse.
            </p>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-3 bg-card p-8 rounded-lg shadow-elegant border border-border space-y-5">
            <div>
              <Label htmlFor="tipo">Tipo di barriera *</Label>
              <Select value={form.tipo_barriera} onValueChange={(v) => setForm({ ...form, tipo_barriera: v })}>
                <SelectTrigger id="tipo"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {tipi.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="descrizione">Descrizione *</Label>
              <Textarea id="descrizione" rows={4} maxLength={2000} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Descrivi la barriera e il contesto..." />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="indirizzo">Indirizzo *</Label>
                <Input id="indirizzo" maxLength={200} value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label htmlFor="citta">Città *</Label>
                  <Input id="citta" maxLength={100} value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="prov">Prov.</Label>
                  <Input id="prov" maxLength={5} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
              <div>
                <Label htmlFor="nome">Nome (opzionale)</Label>
                <Input id="nome" maxLength={100} value={form.nome_segnalante} onChange={(e) => setForm({ ...form, nome_segnalante: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">Email (opzionale)</Label>
                <Input id="email" type="email" maxLength={255} value={form.email_segnalante} onChange={(e) => setForm({ ...form, email_segnalante: e.target.value })} />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-hero text-primary-foreground hover:opacity-90 h-12 text-base font-semibold">
              {loading ? "Invio in corso..." : "Invia segnalazione"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Segnalazioni;
